(() => {
  'use strict';

  const MAX_FILE_BYTES = 10 * 1024 * 1024;
  const MAX_IMAGE_PIXELS = 40 * 1000 * 1000;
  const MAX_IMAGE_EDGE = 8000;
  const MIME_TYPES = new Set(['image/jpeg', 'image/png']);

  class PersonalizerError extends Error {
    constructor(code, message) {
      super(message);
      this.name = 'PersonalizerError';
      this.code = code;
    }
  }

  const abortError = () => new DOMException('The request was cancelled.', 'AbortError');
  const assertActive = (signal) => { if (signal?.aborted) throw signal.reason || abortError(); };

  function abortable(promise, signal) {
    assertActive(signal);
    if (!signal) return Promise.resolve(promise);
    return new Promise((resolve, reject) => {
      const onAbort = () => {
        signal.removeEventListener('abort', onAbort);
        reject(signal.reason || abortError());
      };
      signal.addEventListener('abort', onAbort, { once: true });
      Promise.resolve(promise).then(resolve, reject).finally(() => signal.removeEventListener('abort', onAbort));
    });
  }

  function delay(milliseconds, signal) {
    assertActive(signal);
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        signal?.removeEventListener('abort', onAbort);
        resolve();
      }, milliseconds);
      const onAbort = () => {
        clearTimeout(timer);
        reject(signal.reason || abortError());
      };
      signal?.addEventListener('abort', onAbort, { once: true });
    });
  }

  function servicePath(value, origin) {
    if (typeof value !== 'string' || !/^\/apps\/[a-zA-Z0-9_-]+(?:\/[a-zA-Z0-9_-]+)*\/?$/.test(value)) return null;
    const url = new URL(value, origin);
    return url.origin === origin && url.protocol === 'https:' ? url.pathname.replace(/\/$/, '') : null;
  }

  function imageUrl(value, origin) {
    if (typeof value !== 'string' || !value || value.length > 4096 || /[\u0000-\u0020\\]/.test(value)) return null;
    try {
      const url = new URL(value, origin);
      return url.protocol === 'https:' && !url.username && !url.password ? url.href : null;
    } catch (_) {
      return null;
    }
  }

  function signatureType(bytes) {
    if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image/jpeg';
    const png = [137, 80, 78, 71, 13, 10, 26, 10];
    return png.every((byte, index) => bytes[index] === byte) ? 'image/png' : null;
  }

  function encodedDimensions(bytes, type) {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    if (type === 'image/png') {
      if (bytes.length < 24 || view.getUint32(12) !== 0x49484452 || view.getUint32(8) !== 13) return null;
      return { width: view.getUint32(16), height: view.getUint32(20) };
    }
    let offset = 2;
    const frameMarkers = new Set([0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf]);
    while (offset + 3 < bytes.length) {
      if (bytes[offset++] !== 0xff) return null;
      while (bytes[offset] === 0xff) offset += 1;
      const marker = bytes[offset++];
      if (marker === 0xda || marker === 0xd9) return null;
      if (marker >= 0xd0 && marker <= 0xd7) continue;
      if (offset + 2 > bytes.length) return null;
      const length = view.getUint16(offset);
      if (length < 2 || offset + length > bytes.length) return null;
      if (frameMarkers.has(marker)) {
        return length >= 8 ? { width: view.getUint16(offset + 5), height: view.getUint16(offset + 3) } : null;
      }
      offset += length;
    }
    return null;
  }

  function checkDimensions(dimensions) {
    const { width, height } = dimensions || {};
    if (!Number.isInteger(width) || !Number.isInteger(height) || width < 256 || height < 256 ||
        width > MAX_IMAGE_EDGE || height > MAX_IMAGE_EDGE || width * height > MAX_IMAGE_PIXELS) {
      throw new PersonalizerError('dimensions', 'Choose a photo between 256 and 8,000 pixels per side, up to 40 megapixels.');
    }
  }

  async function decodeImage(file) {
    if (typeof createImageBitmap === 'function') {
      const bitmap = await createImageBitmap(file);
      try { return { width: bitmap.width, height: bitmap.height }; }
      finally { bitmap.close(); }
    }
    const url = URL.createObjectURL(file);
    const image = new Image();
    try {
      image.src = url;
      await image.decode();
      return { width: image.naturalWidth, height: image.naturalHeight };
    } finally {
      image.removeAttribute('src');
      URL.revokeObjectURL(url);
    }
  }

  async function inspectImage(file, { signal, decode = decodeImage } = {}) {
    assertActive(signal);
    if (!file || !MIME_TYPES.has(file.type)) throw new PersonalizerError('type', 'Choose a JPG or PNG photo.');
    if (!Number.isFinite(file.size) || file.size <= 0 || file.size > MAX_FILE_BYTES) {
      throw new PersonalizerError('size', 'Choose a photo smaller than 10 MB.');
    }
    const bytes = new Uint8Array(await abortable(file.slice(0, 256 * 1024).arrayBuffer(), signal));
    if (signatureType(bytes) !== file.type) throw new PersonalizerError('signature', 'This file is not a valid JPG or PNG photo.');
    const encoded = encodedDimensions(bytes, file.type);
    if (!encoded) throw new PersonalizerError('header', 'This photo could not be read safely. Choose another JPG or PNG.');
    checkDimensions(encoded);
    let dimensions;
    try { dimensions = await abortable(decode(file), signal); }
    catch (error) {
      assertActive(signal);
      throw new PersonalizerError('decode', 'This photo could not be opened. Choose another JPG or PNG.');
    }
    assertActive(signal);
    const { width, height } = dimensions;
    checkDimensions(dimensions);
    return { width, height, type: file.type, size: file.size };
  }

  function exactVariant(variants, values) {
    return variants.find((variant) => Array.isArray(variant.options) && variant.options.length === values.length &&
      variant.options.every((value, index) => value === values[index])) || null;
  }

  async function encodeUpload(file) {
    if (typeof createImageBitmap !== 'function' || typeof document === 'undefined' || typeof document.createElement !== 'function') return null;
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const canvas = document.createElement('canvas');
    try {
      const scale = Math.min(1, 2048 / Math.max(bitmap.width, bitmap.height));
      canvas.width = Math.round(bitmap.width * scale);
      canvas.height = Math.round(bitmap.height * scale);
      if (Math.min(canvas.width, canvas.height) < 256) return null;
      const context = canvas.getContext('2d', { alpha: false });
      if (!context) return null;
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      return await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
    } finally {
      bitmap.close();
      canvas.width = canvas.height = 1;
    }
  }

  async function prepareUpload(file, { signal, encode = encodeUpload } = {}) {
    assertActive(signal);
    // Inspect the original first. Keep its UI preview and prepare exactly once
    // so idempotent retries send identical bytes, not fresh lossy encodings.
    if (file.size < 300 * 1024) return file;
    try {
      const candidate = await abortable(encode(file), signal);
      assertActive(signal);
      return candidate?.type === 'image/jpeg' && candidate.size > 0 && candidate.size < file.size * 0.9 ? candidate : file;
    } catch (error) {
      assertActive(signal);
      return file; // Unsupported canvas/codec retains the validated server path.
    }
  }

  function validateTiming(value) {
    if (!value || value.kind !== 'estimate' || !['preparing', 'queued', 'generating', 'finalizing', 'complete', 'failed'].includes(value.phase)) return null;
    const limits = { elapsed_ms: 3600000, total_elapsed_ms: 3600000, expected_ms: 180000,
      typical_low_ms: 180000, typical_high_ms: 180000, sample_count: 30, queue_ahead: 1000, queue_wait_ms: 3600000 };
    if (Object.entries(limits).some(([key, max]) => !Number.isInteger(value[key]) || value[key] < 0 || value[key] > max) ||
        value.expected_ms < 1000 || value.typical_low_ms > value.typical_high_ms) return null;
    return Object.fromEntries(['phase', ...Object.keys(limits)].map((key) => [key, value[key]]));
  }

  /** Time-based feedback is explicitly an estimate, never an AI percentage. */
  function progressPresentation(state, timing, ageMs = 0, waiting = null) {
    // Saving the result and an expired estimate are normal parts of waiting.
    // Only a real queue or stale update uses the separate problem presentation.
    const exceptional = state === 'generating' && (ageMs > 8000 ||
      timing?.phase === 'queued' && timing.queue_ahead > 0);
    if (waiting?.preparing && !exceptional && ['validating', 'uploading', 'generating'].includes(state)) {
      return { title: 'Getting your photo ready', unified: true, uploading: true,
        percent: null, detail: 'A little magic starts with your photo.', average: '' };
    }
    if (waiting && !exceptional && ['validating', 'uploading', 'generating', 'finalizing'].includes(state)) {
      const remaining = Math.max(0, waiting.deadline - waiting.now);
      const seconds = Math.ceil(remaining / 1000);
      const elapsed = Math.max(0, waiting.now - waiting.startedAt);
      const duration = Math.max(1000, waiting.deadline - waiting.startedAt);
      return { title: 'Creating your painting', unified: true,
        percent: Math.min(94, 10 + Math.floor(85 * (1 - Math.exp(-elapsed / (duration * 0.65))))),
        detail: 'A little magic is on the way.\n' + (seconds > 1 ? `Ready in about ${seconds} seconds.` : 'Almost ready. Just a little longer.'), average: '' };
    }
    const age = Math.max(0, Number.isFinite(ageMs) ? ageMs : 0);
    const elapsed = (timing?.elapsed_ms || 0) + age;
    const average = timing?.sample_count >= 3 ? `Recent average: ${Math.round(timing.expected_ms / 1000)} sec. Timing can vary.` : 'Usually about 20–35 sec after upload. Timing can vary.';
    if (state === 'complete') return { title: 'Your preview is ready', percent: 100, detail: '', average: '' };
    if (state === 'validating') return { title: 'Preparing your photo', percent: null, detail: 'Checking and optimizing your upload…', average };
    if (state === 'uploading') return { title: 'Uploading your photo', percent: null, detail: 'Keep this page open while your photo uploads.', average };
    if (state === 'finalizing') return { title: 'Loading your preview', percent: 97, detail: 'Your painting is generated. Preparing it for you…', average };
    if (!timing) return { title: 'Creating your painting', percent: null, detail: 'Waiting for an update from the preview service…', average };
    if (age > 8000) return { title: 'Checking your connection', percent: null, detail: 'Waiting for the latest update. Your preview will not restart.', average };
    if (timing.phase === 'queued' || timing.phase === 'preparing') return {
      title: 'Your photo is ready', percent: null,
      detail: timing.queue_ahead && timing.queue_wait_ms - age > 1000 ? `Waiting for a free spot. About ${Math.ceil((timing.queue_wait_ms - age) / 1000)} sec before generation.` : 'Starting your painting…', average
    };
    if (timing.phase === 'finalizing') return { title: 'Finishing your preview', percent: 95, detail: 'Saving your painting…', average };
    const expected = timing.expected_ms;
    const percent = Math.min(94, 10 + Math.floor(85 * (1 - Math.exp(-elapsed / (expected * 0.65)))));
    const seconds = Math.ceil((expected - elapsed) / 1000);
    const detail = seconds > 1 ? `About ${seconds} sec remaining` : 'Almost ready. Just a little longer.';
    return { title: 'Creating your painting', percent, detail, average };
  }

  function resolveVariant(variants, values, changedIndex) {
    const exact = exactVariant(variants, values);
    if (exact?.available) return exact;
    const candidates = variants.filter((variant) => variant.available && variant.options?.[changedIndex] === values[changedIndex]);
    candidates.sort((left, right) => {
      const score = (variant) => variant.options.reduce((total, value, index) => total + (value === values[index] ? 1 : 0), 0);
      return score(right) - score(left);
    });
    return candidates[0] || exact;
  }

  function validateJob(payload, origin) {
    const states = ['queued', 'processing', 'complete', 'failed'];
    if (!payload || typeof payload !== 'object' || !states.includes(payload.status) ||
        typeof payload.id !== 'string' || !/^[a-zA-Z0-9_-]{1,128}$/.test(payload.id)) {
      throw new PersonalizerError('response', 'The preview service returned an invalid response. Please try again.');
    }
    if (payload.status === 'failed') {
      if (payload.error_code === 'TEST_BUDGET_LIMIT') throw new PersonalizerError('limit', 'Preview generation is temporarily paused. Please come back later. Your photo was not transformed.');
      throw new PersonalizerError('generation', 'We could not create your painting. Your photo has not been turned into a preview. Please try again.');
    }
    const progress = typeof payload.progress === 'number' && Number.isFinite(payload.progress) && payload.progress >= 0 && payload.progress <= 1
      ? payload.progress : null;
    const result = { id: payload.id, status: payload.status, progress, timing: validateTiming(payload.timing), templateId: payload.template_id, providerMode: payload.provider_mode };
    if (payload.status === 'complete') {
      result.resultPath = servicePath(payload.resultPath, origin);
      if (!result.resultPath || !result.resultPath.endsWith(`/jobs/${result.id}/result`) ||
          !/^[a-z0-9_]{1,80}$/.test(result.templateId || '') || payload.result_reference !== result.id) {
        throw new PersonalizerError('response', 'The completed preview is unavailable. Please try again.');
      }
    }
    return result;
  }

  /** Visitor capabilities are scoped to this origin/tab, never Shopify app keys.
   * Cookies are not relied on: Shopify strips them at the app-proxy boundary.
   * An empty service path disables every upload/generation network request.
   */
  function createAdapter(base, origin, fetcher = fetch) {
    const path = servicePath(base, origin);
    if (!path) return null;
    const storageKey = `ai-pbn-session-v1:${path}`;
    let session = null;
    try { session = JSON.parse(globalThis.sessionStorage?.getItem(storageKey) || 'null'); } catch (_) { /* Private mode may deny storage. */ }
    const validSession = () => session && /^[A-Za-z0-9_-]{43}$/.test(session.token) &&
      Number.isFinite(session.expires_at) && session.expires_at * 1000 > Date.now() + 30000;
    if (!validSession()) session = null;
    const request = async (url, options, binary = false) => {
      const response = await fetcher(url, { ...options, credentials: 'same-origin', redirect: 'error', cache: 'no-store' });
      if (!response.ok) {
        if (response.status === 401) {
          session = null;
          try { globalThis.sessionStorage?.removeItem(storageKey); } catch (_) { /* Memory-only mode. */ }
          throw new PersonalizerError('session', 'Your preview session expired. Choose your photo again to start a new preview.');
        }
        if (response.status === 429) throw new PersonalizerError('limit', 'The preview limit has been reached. Please try again later.');
        throw new PersonalizerError('service', 'The preview service is unavailable. Please try again.');
      }
      const type = response.headers.get('content-type')?.split(';')[0];
      if (type !== (binary ? 'image/jpeg' : 'application/json')) throw new PersonalizerError('response', 'The preview service returned an invalid response.');
      const maximum = binary ? 2 * 1024 * 1024 : 16384;
      if (Number(response.headers.get('content-length')) > maximum) throw new PersonalizerError('response', 'The preview response is too large.');
      const reader = response.body.getReader();
      const chunks = [];
      let length = 0;
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          length += value.byteLength;
          if (length > maximum) throw new PersonalizerError('response', 'The preview response is too large.');
          chunks.push(value);
        }
      } finally { await reader.cancel(); reader.releaseLock(); }
      const blob = new Blob(chunks, { type });
      if (binary) {
        await inspectImage(blob, { signal: options.signal });
        return blob;
      }
      try { return JSON.parse(await blob.text()); }
      catch (_) { throw new PersonalizerError('response', 'The preview service returned an invalid response.'); }
    };
    const headers = async (signal) => {
      if (!validSession()) {
        if (session) throw new PersonalizerError('session', 'Your preview session expired. Reload the page to start again.');
        session = await request(`${path}/session`, { method: 'POST', signal, headers: { Accept: 'application/json' } });
        if (!validSession()) throw new PersonalizerError('response', 'The preview session could not be started.');
        try { globalThis.sessionStorage?.setItem(storageKey, JSON.stringify(session)); } catch (_) { /* Memory-only fallback. */ }
      }
      return { Accept: 'application/json', Authorization: `Bearer ${session.token}` };
    };
    return {
      async create({ file, productId, variantId, attemptKey }, signal) {
        if (!/^[A-Za-z0-9._:-]{16,128}$/.test(attemptKey || '')) throw new PersonalizerError('attempt', 'Start a new photo preview.');
        const body = new FormData();
        body.set('photo', file, file.type === 'image/png' ? 'photo.png' : 'photo.jpg');
        body.set('product_id', String(productId));
        body.set('variant_id', String(variantId));
        return request(`${path}/jobs`, { method: 'POST', body, signal, headers: { ...await headers(signal), 'Idempotency-Key': attemptKey } });
      },
      async status(id, signal) {
        if (!/^[a-zA-Z0-9_-]{1,128}$/.test(id)) throw new PersonalizerError('response', 'Invalid preview reference.');
        return request(`${path}/jobs/${encodeURIComponent(id)}`, { method: 'GET', signal, headers: await headers(signal) });
      },
      async result(job, signal) {
        if (job.resultPath !== `${path}/jobs/${job.id}/result`) throw new PersonalizerError('response', 'Invalid preview location.');
        return request(job.resultPath, { method: 'GET', signal, headers: { ...await headers(signal), Accept: 'image/jpeg' } }, true);
      },
      async selection(id, variantId, signal) {
        if (!/^[a-zA-Z0-9_-]{1,128}$/.test(id)) throw new PersonalizerError('response', 'Invalid preview reference.');
        return request(`${path}/jobs/${id}/selection`, { method: 'POST', signal,
          headers: { ...await headers(signal), 'Content-Type': 'application/json' }, body: JSON.stringify({ variant_id: String(variantId) }) });
      }
    };
  }

  function createJobJournal(base, origin, productId, storage) {
    const path = servicePath(base, origin);
    if (!path || !/^[1-9][0-9]{0,19}$/.test(String(productId))) return null;
    try { storage = storage || globalThis.sessionStorage; } catch (_) { return null; }
    if (!storage) return null;
    const key = `ai-pbn-job-v1:${path}:${productId}`;
    const clear = () => { try { storage.removeItem(key); } catch (_) { /* Storage may be disabled. */ } };
    return {
      clear,
      save(id) {
        if (!/^[a-zA-Z0-9_-]{1,128}$/.test(id)) return;
        try { storage.setItem(key, JSON.stringify({ id, expires: Date.now() + 23 * 60 * 60 * 1000 })); } catch (_) { /* No photo or filename is persisted. */ }
      },
      read() {
        try {
          const record = JSON.parse(storage.getItem(key) || 'null');
          if (record && /^[a-zA-Z0-9_-]{1,128}$/.test(record.id) && Number.isFinite(record.expires) && record.expires > Date.now() && record.expires <= Date.now() + 24 * 60 * 60 * 1000) return record.id;
        } catch (_) { /* Invalid records cannot trigger requests. */ }
        clear(); return null;
      }
    };
  }

  class PreviewJob {
    constructor(adapter, { origin, onState = () => {}, onAccepted = () => {}, timeoutMs = 120000, pollMs = 1000, wait = delay } = {}) {
      Object.assign(this, { adapter, origin, onState, onAccepted, timeoutMs, pollMs, wait });
      this.revision = 0;
    }

    cancel() {
      this.revision += 1;
      this.controller?.abort();
      this.controller = null;
    }

    async run(input, resumeId = null) {
      this.cancel();
      if (!this.adapter) throw new PersonalizerError('unconfigured', 'AI preview is currently unavailable. Your photo has not been uploaded.');
      const revision = this.revision;
      const controller = new AbortController();
      this.controller = controller;
      const { signal } = controller;
      const timer = setTimeout(() => controller.abort(new PersonalizerError('timeout', 'Creating the preview took too long. Please try again.')), this.timeoutMs);
      const check = () => {
        assertActive(signal);
        if (revision !== this.revision) throw abortError();
      };
      try {
        if (resumeId !== null && !/^[a-zA-Z0-9_-]{1,128}$/.test(resumeId)) throw new PersonalizerError('response', 'Invalid saved preview. Choose your photo again.');
        this.onState({ state: resumeId ? 'generating' : 'uploading', progress: null });
        let job = validateJob(await abortable(resumeId ? this.adapter.status(resumeId, signal) : this.adapter.create(input, signal), signal), this.origin);
        check();
        if (resumeId && job.id !== resumeId) throw new PersonalizerError('response', 'The saved preview could not be confirmed.');
        this.onAccepted(job.id);
        const id = job.id;
        while (job.status !== 'complete') {
          this.onState({ state: 'generating', progress: job.progress, timing: job.timing });
          await this.wait(this.pollMs, signal);
          check();
          job = validateJob(await abortable(this.adapter.status(id, signal), signal), this.origin);
          check();
          if (job.id !== id) throw new PersonalizerError('response', 'The preview service returned a different photo. Please try again.');
        }
        if (job.providerMode !== 'openai-gpt-image-2') throw new PersonalizerError('demo', 'AI generation is not enabled yet. No AI preview has been created.');
        this.onState({ state: 'finalizing', progress: null, timing: job.timing });
        const resultBlob = await abortable(this.adapter.result(job, signal), signal);
        check();
        return { ...job, resultBlob };
      } finally {
        clearTimeout(timer);
        if (this.controller === controller) this.controller = null;
      }
    }
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = { PersonalizerError, servicePath, imageUrl, signatureType, encodedDimensions, inspectImage, prepareUpload, exactVariant, resolveVariant, validateJob, validateTiming, progressPresentation, createAdapter, createJobJournal, PreviewJob };
  }
  if (typeof window !== 'undefined' && !window.MyArtAiPbnCore) {
    window.MyArtAiPbnCore = Object.freeze({ inspectImage, prepareUpload, createAdapter, createJobJournal, PreviewJob, progressPresentation });
  }
  if (typeof customElements === 'undefined' || customElements.get('ai-pbn-personalizer')) return;

  class AiPbnPersonalizer extends HTMLElement {
    connectedCallback() {
      if (this.lifecycle) return;
      this.lifecycle = new AbortController();
      const { signal } = this.lifecycle;
      this.form = this.closest('form') || document.getElementById(this.dataset.formId);
      this.card = this.closest('[data-ai-pbn-product-card-root]');
      this.select = this.form?.querySelector('[data-pbn-variant-select], [data-ai-pbn-variant-select]');
      this.fileInput = this.querySelector('[data-ai-file]:not([data-ai-camera-file])');
      this.dialogs = Array.from(this.querySelectorAll('dialog'));
      this.revision = 0;
      this.fileRevision = 0;
      this.zoom = 1;
      this.file = null;
      this.restored = false;
      this.result = null;
      this.sourceObjectUrl = null;
      this.resultObjectUrl = null;
      this.attemptKey = null;
      this.attemptVariantId = null;
      this.inputModality = 'pointer';
      try { this.variants = JSON.parse(this.querySelector('[data-ai-variants]')?.textContent || '[]'); }
      catch (_) { this.variants = []; }
      if (!(this.form instanceof HTMLFormElement) || !(this.select instanceof HTMLSelectElement) || !Array.isArray(this.variants)) return;
      this.adapter = createAdapter(this.dataset.servicePath || '', window.location.origin);
      this.journal = createJobJournal(this.dataset.servicePath || '', window.location.origin, this.card?.dataset.productId);
      this.job = new PreviewJob(this.adapter, { origin: window.location.origin,
        onState: (state) => this.showState(state.state, state.progress, state.timing), onAccepted: (id) => this.journal?.save(id) });
      this.initializeMotion();
      this.initializeViewer();

      this.form.addEventListener('submit', (event) => this.guardPurchase(event), { capture: true, signal });
      this.card?.addEventListener('click', (event) => {
        if (!(event.target instanceof Element) || !event.target.closest('.productcard__item-buy, .totalCardBar__item-buy')) return;
        this.guardPurchase(event);
        if (!event.defaultPrevented && event.target.closest('.totalCardBar__item-buy')) {
          // Forward the photo-gated sticky action to the one canonical form.
          // Do not let a second host click-forwarder submit the same selection.
          event.preventDefault();
          event.stopImmediatePropagation();
          const main = this.card.querySelector('.productcard__item-buy');
          if (main && !main.disabled) this.form.requestSubmit(main);
        }
      }, { capture: true, signal });
      this.form.addEventListener('formdata', (event) => {
        // Only the verified, processed image becomes Shopify's persistent upload.
        // Source inputs deliberately have no names; never attach this.file/uploadFile.
        event.formData.delete('properties[Photo]');
        event.formData.delete('properties[Original photo]');
        if (!this.purchaseReady()) {
          this.querySelectorAll('[data-ai-property]').forEach((input) => event.formData.delete(input.name));
        } else if (this.result.resultBlob instanceof Blob) {
          const extension = this.result.resultBlob.type === 'image/png' ? 'png' : 'jpg';
          event.formData.set('properties[Photo]', this.result.resultBlob, `your-ai-painting.${extension}`);
        }
      }, { signal });
      this.addEventListener('keydown', () => { this.inputModality = 'keyboard'; }, { signal });
      this.addEventListener('pointerdown', () => { this.inputModality = 'pointer'; }, { signal });
      this.addEventListener('click', (event) => this.onClick(event), { signal });
      this.querySelectorAll('[data-ai-file]').forEach((input) => {
        input.addEventListener('change', () => { if (input.files?.[0]) this.chooseFile(input.files[0]); }, { signal });
      });
      const dropzone = this.querySelector('[data-ai-upload-empty]');
      const fileDrag = (event) => Array.from(event.dataTransfer?.types || []).includes('Files');
      dropzone?.addEventListener('dragover', (event) => {
        if (!fileDrag(event)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
        dropzone.setAttribute('data-ai-dragover', '');
      }, { signal });
      dropzone?.addEventListener('dragleave', (event) => {
        if (!(event.relatedTarget instanceof Node) || !dropzone.contains(event.relatedTarget)) dropzone.removeAttribute('data-ai-dragover');
      }, { signal });
      dropzone?.addEventListener('drop', (event) => {
        if (!fileDrag(event)) return;
        event.preventDefault();
        event.stopPropagation();
        dropzone.removeAttribute('data-ai-dragover');
        if (event.dataTransfer.files.length === 1) this.chooseFile(event.dataTransfer.files[0]);
        else this.showError('Choose one photo at a time.');
      }, { signal });
      this.select.addEventListener('change', () => {
        this.clearProperties();
        if (this.result) this.bindSelection();
        // The host may update availability later in this same change dispatch.
        Promise.resolve().then(() => { if (!signal.aborted) this.syncPurchaseControls(); });
      }, { signal });
      if (typeof MutationObserver === 'function') {
        this.purchaseObserver = new MutationObserver(() => this.syncPurchaseControls());
        this.purchaseControls().forEach((button) => {
          this.purchaseObserver.observe(button, { attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'aria-busy', 'class'] });
        });
        this.purchaseObserver.observe(this.form, { attributes: true, attributeFilter: ['data-cart-submit-pending'] });
        signal.addEventListener('abort', () => this.purchaseObserver?.disconnect(), { once: true });
      }
      this.dialogs.forEach((dialog) => {
        dialog.addEventListener('cancel', event => { event.preventDefault(); this.inputModality = 'keyboard'; this.closeDialog(dialog); }, { signal });
        dialog.addEventListener('close', () => this.afterDialogClose(dialog), { signal });
        dialog.addEventListener('click', (event) => {
          if (event.target !== dialog) return;
          const rect = dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closeDialog(dialog);
        }, { signal });
      });
      this.clearProperties();
      this.showState('empty');
      this.classList.add('is-enhanced');
      const saved = this.journal?.read();
      if (saved) this.restoreJob(saved);
    }

    async restoreJob(id) {
      this.invalidateResult();
      const revision = this.revision;
      this.restored = true;
      this.querySelectorAll('[data-ai-file-name]').forEach((node) => { node.textContent = 'Previously uploaded photo'; });
      this.querySelectorAll('[data-ai-file-size]').forEach((node) => { node.textContent = ''; });
      try {
        const result = await this.job.run(null, id);
        if (revision !== this.revision || !this.isConnected) return;
        this.result = { ...result, variantId: null, revision };
        this.resultObjectUrl = URL.createObjectURL(result.resultBlob);
        this.setPreview(this.resultObjectUrl);
        this.showState('complete');
        await this.bindSelection();
      } catch (error) {
        if (revision !== this.revision || error.name === 'AbortError') return;
        if (['session', 'generation', 'demo', 'response', 'limit'].includes(error.code)) this.journal?.clear();
        this.retryBlocked = error.code === 'limit';
        this.showError(this.errorMessage(error));
      }
    }

    selectedVariant() { return this.variants.find((variant) => String(variant.id) === this.select?.value) || null; }

    purchaseControls() {
      return Array.from(this.card?.querySelectorAll?.('.productcard__item-buy, .totalCardBar__item-buy') || []);
    }

    purchasePending() {
      const main = this.card?.querySelector?.('.productcard__item-buy');
      return this.form?.dataset.cartSubmitPending === 'true' || main?.getAttribute('aria-busy') === 'true' ||
        Boolean(main?.classList.contains('loading'));
    }

    syncPurchaseControls() {
      const ready = this.purchaseReady();
      const processing = ['validating', 'uploading', 'generating', 'finalizing'].includes(this.dataset.aiState);
      // Let a missing-photo attempt reach the existing capture-phase purchase guard.
      // Pending writes, processing and unavailable variants remain native-disabled.
      const missingPhoto = !this.file && !this.restored && !processing && this.selectedVariant()?.available;
      const disabled = this.purchasePending() || (!ready && !missingPhoto);
      if (ready) this.purchaseHelpRequested = false;
      const help = this.card?.querySelector?.('[data-ai-purchase-help]');
      if (help) {
        const message = !this.purchaseHelpRequested || ready ? '' : processing || this.restored && !this.result && this.dataset.aiState !== 'error'
          ? 'Your AI preview is being prepared. You can add it to cart when it is ready.'
          : !this.file && !this.restored
            ? 'Upload your photo above to create your AI preview before adding to cart.'
            : !this.selectedVariant()?.available
              ? 'Choose an available option before adding to cart.'
              : 'Complete your AI preview above before adding to cart.';
        if (help.textContent !== message) help.textContent = message;
        help.hidden = !message;
      }
      this.purchaseControls().forEach((button) => {
        if (help?.id) {
          const ids = new Set((button.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
          ids.add(help.id);
          button.setAttribute('aria-describedby', [...ids].join(' '));
        }
        if (button.disabled !== disabled) button.disabled = disabled;
        if (disabled && button.getAttribute('aria-disabled') !== 'true') button.setAttribute('aria-disabled', 'true');
        else if (!disabled && button.hasAttribute('aria-disabled')) button.removeAttribute('aria-disabled');
      });
    }

    clearProperties() {
      this.querySelectorAll('[data-ai-property]').forEach((input) => { input.value = ''; input.disabled = true; });
    }

    invalidateResult(showReady = true) {
      this.revision += 1;
      this.job?.cancel();
      this.selectionRequest?.abort();
      this.result = null;
      if (this.resultObjectUrl) URL.revokeObjectURL(this.resultObjectUrl);
      this.resultObjectUrl = null;
      this.clearProperties();
      this.syncPurchaseControls();
      if (this.file) {
        this.setPreview(this.sourceObjectUrl);
        if (showReady) this.showState('ready');
      }
    }

    async chooseFile(file) {
      this.reset();
      const revision = this.fileRevision;
      const controller = new AbortController();
      this.validation = controller;
      this.showState('validating');
      try {
        await inspectImage(file, { signal: controller.signal });
        const uploadFile = await prepareUpload(file, { signal: controller.signal });
        if (revision !== this.fileRevision || !this.isConnected) return;
        this.file = file;
        this.uploadFile = uploadFile;
        this.sourceObjectUrl = URL.createObjectURL(file);
        this.querySelectorAll('[data-ai-file-name]').forEach((node) => { node.textContent = file.name; });
        this.querySelectorAll('[data-ai-file-size]').forEach((node) => { node.textContent = `${(file.size / 1024 / 1024).toFixed(2)} MB`; });
        this.setPreview(this.sourceObjectUrl);
        if (this.adapter) await this.generate();
        else {
          this.showState('ready');
          this.showError('AI preview is currently unavailable. Your photo has not been uploaded.');
        }
      } catch (error) {
        if (revision === this.fileRevision && error.name !== 'AbortError') this.showError(this.errorMessage(error));
      } finally {
        if (this.validation === controller) this.validation = null;
      }
    }

    async generate() {
      if (!this.file) { this.showError('Choose a photo to create your preview.'); return; }
      this.invalidateResult(false);
      const revision = this.revision;
      const variant = this.selectedVariant();
      if (!variant?.available) { this.showError('Choose an available option.'); return; }
      if (!this.attemptKey) {
        this.attemptKey = globalThis.crypto.randomUUID();
        this.attemptVariantId = variant.id;
      }
      try {
        const result = await this.job.run({ file: this.uploadFile || this.file, productId: this.card?.dataset.productId,
          variantId: this.attemptVariantId, attemptKey: this.attemptKey });
        if (revision !== this.revision || !this.isConnected) return;
        this.result = { ...result, variantId: null, revision };
        this.resultObjectUrl = URL.createObjectURL(result.resultBlob);
        this.setPreview(this.resultObjectUrl);
        this.showState('complete');
        await this.bindSelection();
      } catch (error) {
        if (revision === this.revision && ['session', 'generation', 'demo', 'limit'].includes(error.code)) this.journal?.clear();
        if (revision === this.revision && error.code === 'generation') this.attemptKey = null;
        if (revision === this.revision && error.name !== 'AbortError') {
          this.retryBlocked = error.code === 'limit';
          this.showError(this.errorMessage(error));
        }
      }
    }

    async bindSelection() {
      this.selectionRequest?.abort();
      this.clearProperties();
      this.syncPurchaseControls();
      const result = this.result;
      const variant = this.selectedVariant();
      if (!result || !variant?.available) return;
      const controller = new AbortController();
      this.selectionRequest = controller;
      const timer = setTimeout(() => controller.abort(new PersonalizerError('timeout', 'Confirming your selection took too long. Please try again.')), 15000);
      try {
        const selection = await abortable(this.adapter.selection(result.id, variant.id, controller.signal), controller.signal);
        if (this.result !== result || String(variant.id) !== this.select.value || !this.isConnected) return;
        if (selection.job_id !== result.id || selection.variant_id !== String(variant.id) ||
            selection.product_id !== String(this.card?.dataset.productId) || selection.template_id !== result.templateId ||
            selection.provider_mode !== 'openai-gpt-image-2' || !/^[a-f0-9]{64}$/.test(selection.proof || '')) {
          throw new PersonalizerError('response', 'Your selection could not be confirmed. Please try again.');
        }
        result.variantId = this.select.value;
        const values = { job: result.id, template: result.templateId, proof: selection.proof };
        this.querySelectorAll('[data-ai-property]').forEach((input) => {
          const value = values[input.dataset.aiProperty];
          if (typeof value === 'string' && input.name.startsWith('properties[')) { input.value = value; input.disabled = false; }
        });
        this.showState('complete');
      } catch (error) {
        if (this.result === result && this.selectionRequest === controller && String(variant.id) === this.select.value &&
            this.isConnected && error.name !== 'AbortError') this.showError(this.errorMessage(error));
      } finally {
        clearTimeout(timer);
        if (this.selectionRequest === controller) this.selectionRequest = null;
      }
    }

    purchaseReady() {
      const propertiesReady = ['template', 'proof', 'job'].every((key) => {
        const input = this.querySelector(`[data-ai-property="${key}"]`);
        return input && !input.disabled && Boolean(input.value) && input.name.startsWith('properties[');
      });
      return Boolean(this.result && (this.file || this.restored) && this.result.revision === this.revision &&
        this.result.variantId === this.select.value && this.selectedVariant()?.available && this.dataset.aiState === 'complete' && propertiesReady);
    }

    guardPurchase(event) {
      if (this.purchasePending()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (this.purchaseReady()) {
        if (event.type === 'submit') {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.addPaintingToCart();
        }
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      this.purchaseHelpRequested = true;
      this.syncPurchaseControls();
      this.card?.querySelector?.('[data-ai-purchase-help]')?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }

    async addPaintingToCart() {
      if (this.purchasePending() || !this.purchaseReady()) return;
      const cart = document.querySelector('cart-drawer.myart-cart-host');
      if (typeof cart?.addFromForm !== 'function') {
        this.showError('The cart is still loading. Please try adding your painting again.', false);
        return;
      }
      // Construct before disabling controls: FormData fires our final-only attachment hook.
      const payload = new FormData(this.form);
      const photo = payload.get('properties[Photo]');
      if (!(photo instanceof Blob) || photo.size === 0) {
        this.showError('Your finished painting could not be attached. Please try again.', false);
        return;
      }
      const controls = Array.from(this.form.querySelectorAll('input, select, button'));
      const disabled = controls.map((control) => control.disabled);
      const purchaseButtons = this.purchaseControls();
      const purchaseStates = purchaseButtons.map((button) => ({
        button,
        disabled: button.disabled,
        busy: button.getAttribute('aria-busy'),
        ariaDisabled: button.getAttribute('aria-disabled'),
        loading: button.classList.contains('loading'),
      }));
      const formBusy = this.form.getAttribute('aria-busy');
      this.form.dataset.cartSubmitPending = 'true';
      this.form.setAttribute('aria-busy', 'true');
      controls.forEach((control) => { control.disabled = true; });
      purchaseButtons.forEach((button) => {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('loading');
      });
      this.syncPurchaseControls();
      try {
        // Shared Cart owns its mutation lock, reconciliation and ambiguous-write recovery.
        // Never fall back to another POST if a response is lost or presentation fails.
        const outcome = await cart.whenMerchandiseConfirmed(cart.addFromForm(payload, { open: true, trigger: this.card.querySelector('.productcard__item-buy') }));
        if (!outcome?.ok && this.isConnected) this.showError('Check your cart before trying again. Your painting may already be there.', false);
      } catch (_) {
        if (this.isConnected) this.showError('Check your cart before trying again. Your painting may already be there.', false);
      } finally {
        controls.forEach((control, index) => { control.disabled = disabled[index]; });
        delete this.form.dataset.cartSubmitPending;
        if (formBusy === null) this.form.removeAttribute('aria-busy');
        else this.form.setAttribute('aria-busy', formBusy);
        purchaseStates.forEach(({ button, disabled: wasDisabled, busy, ariaDisabled, loading }) => {
          button.disabled = wasDisabled;
          if (busy === null) button.removeAttribute('aria-busy');
          else button.setAttribute('aria-busy', busy);
          if (ariaDisabled === null) button.removeAttribute('aria-disabled');
          else button.setAttribute('aria-disabled', ariaDisabled);
          button.classList.toggle('loading', loading);
        });
        this.syncPurchaseControls();
      }
    }

    setPreview(url) {
      this.zoom = 1;
      this.querySelectorAll('[data-ai-preview], [data-ai-view-image]').forEach((image) => {
        if (url) image.src = url;
        else image.removeAttribute('src');
        image.style.setProperty('--ai-image-scale', '1');
      });
      const thumbnailUrl = this.sourceObjectUrl || url;
      this.querySelectorAll('[data-ai-thumbnail]').forEach((image) => {
        if (thumbnailUrl) image.src = thumbnailUrl;
        else image.removeAttribute('src');
      });
    }

    initializeViewer() {
      const viewer = this.querySelector('[data-ai-viewer]');
      const stage = this.querySelector('[data-ai-view-stage]');
      if (!viewer || !stage) return;
      this.viewer = viewer;
      this.viewStage = stage;
      this.viewImage = stage.querySelector('[data-ai-view-image]');
      this.viewPointers = new Map();
      this.resetViewer();
      const { signal } = this.lifecycle;
      stage.addEventListener('pointerdown', (event) => {
        if (event.button !== 0 || this.viewPointers.size >= 2) return;
        this.viewPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        stage.setPointerCapture(event.pointerId);
        stage.setAttribute('data-dragging', '');
      }, { signal });
      stage.addEventListener('pointermove', (event) => {
        if (!this.viewPointers.has(event.pointerId)) return;
        const before = Array.from(this.viewPointers.values());
        this.viewPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        const after = Array.from(this.viewPointers.values());
        const center = (points) => ({ x: points.reduce((sum, p) => sum + p.x, 0) / points.length, y: points.reduce((sum, p) => sum + p.y, 0) / points.length });
        const oldCenter = center(before), newCenter = center(after);
        if (before.length === 2) {
          const distance = (points) => Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
          const previous = distance(before);
          if (previous > 1) this.zoomViewer(this.zoom * distance(after) / previous, oldCenter, false);
        }
        this.viewX += newCenter.x - oldCenter.x;
        this.viewY += newCenter.y - oldCenter.y;
        this.paintViewer(false);
      }, { signal });
      const release = (event) => {
        this.viewPointers.delete(event.pointerId);
        if (stage.hasPointerCapture(event.pointerId)) stage.releasePointerCapture(event.pointerId);
        if (!this.viewPointers.size) {
          stage.removeAttribute('data-dragging');
          this.announceViewer();
        }
      };
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((name) => stage.addEventListener(name, release, { signal }));
      stage.addEventListener('wheel', (event) => {
        if (!viewer.open) return;
        event.preventDefault();
        const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? stage.clientHeight : 1);
        this.zoomViewer(this.zoom * Math.exp(-Math.max(-100, Math.min(100, delta)) * 0.004), { x: event.clientX, y: event.clientY }, false);
      }, { signal, passive: false });
      stage.addEventListener('dblclick', (event) => this.zoomViewer(this.zoom > 1 ? 1 : 2, { x: event.clientX, y: event.clientY }), { signal });
      viewer.addEventListener('keydown', (event) => {
        if (event.ctrlKey || event.metaKey || event.altKey) return;
        if (['+', '=', '-', '0'].includes(event.key)) {
          event.preventDefault();
          this.zoomViewer(event.key === '0' ? 1 : this.zoom + (event.key === '-' ? -0.5 : 0.5));
        } else if (this.zoom > 1 && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
          event.preventDefault();
          this.viewX += event.key === 'ArrowLeft' ? 60 : event.key === 'ArrowRight' ? -60 : 0;
          this.viewY += event.key === 'ArrowUp' ? 60 : event.key === 'ArrowDown' ? -60 : 0;
          this.paintViewer(true);
        }
      }, { signal });
      this.viewImage?.addEventListener('load', () => this.resetViewer(), { signal });
      if (typeof ResizeObserver === 'function') {
        this.viewerObserver = new ResizeObserver(() => { if (viewer.open) this.resetViewer(); });
        this.viewerObserver.observe(stage);
      }
    }

    resetViewer() {
      this.zoom = 1;
      this.viewX = 0;
      this.viewY = 0;
      this.viewPointers?.forEach((_, id) => { if (this.viewStage?.hasPointerCapture(id)) this.viewStage.releasePointerCapture(id); });
      this.viewPointers?.clear();
      this.viewStage?.removeAttribute('data-dragging');
      this.paintViewer(false);
    }

    zoomViewer(value, point = null, animate = true) {
      if (!this.viewStage || !Number.isFinite(value)) return;
      const rect = this.viewStage.getBoundingClientRect();
      const next = Math.max(1, Math.min(4, value));
      const ratio = next / this.zoom;
      const x = point ? point.x - rect.left - rect.width / 2 : 0;
      const y = point ? point.y - rect.top - rect.height / 2 : 0;
      this.viewX = x - (x - this.viewX) * ratio;
      this.viewY = y - (y - this.viewY) * ratio;
      this.zoom = next;
      this.paintViewer(animate);
      if (animate) this.announceViewer();
    }

    paintViewer(animate) {
      if (!this.viewStage || !this.viewImage) return;
      const { width, height } = this.viewStage.getBoundingClientRect();
      const naturalWidth = this.viewImage.naturalWidth || width || 1;
      const naturalHeight = this.viewImage.naturalHeight || height || 1;
      const fit = Math.min(width / naturalWidth, height / naturalHeight);
      const limitX = Math.max(0, (naturalWidth * fit * this.zoom - width) / 2);
      const limitY = Math.max(0, (naturalHeight * fit * this.zoom - height) / 2);
      this.viewX = Math.max(-limitX, Math.min(limitX, this.viewX)) || 0;
      this.viewY = Math.max(-limitY, Math.min(limitY, this.viewY)) || 0;
      this.viewImage.style.transitionDuration = animate && !this.motionMedia?.matches ? '180ms' : '0ms';
      this.viewImage.style.transform = `translate(${this.viewX}px, ${this.viewY}px) scale(${this.zoom})`;
      this.viewStage.dataset.zoomed = String(this.zoom > 1);
      this.viewer.querySelectorAll('[data-ai-action="zoom-out"]').forEach((button) => { button.disabled = this.zoom <= 1; });
      this.viewer.querySelectorAll('[data-ai-action="zoom-in"]').forEach((button) => { button.disabled = this.zoom >= 4; });
    }

    announceViewer() {
      const status = this.viewer?.querySelector('[data-ai-view-scale]');
      if (status) status.textContent = `Zoom ${Math.round(this.zoom * 100)}%`;
    }

    initializeMotion() {
      this.motionMedia = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
      this.motionMedia?.addEventListener?.('change', () => this.stopMotion(), { signal: this.lifecycle.signal });
      const shell = this.querySelector('[data-ai-motion]');
      const stage = this.querySelector('[data-ai-stage]');
      if (!shell || !stage || !this.motionMedia || typeof ResizeObserver !== 'function' || typeof shell.animate !== 'function') return;
      let previousHeight = stage.getBoundingClientRect().height;
      let previousWidth = stage.getBoundingClientRect().width;
      this.stageObserver = new ResizeObserver(() => {
        const { height, width } = stage.getBoundingClientRect();
        const from = this.heightAnimation ? shell.getBoundingClientRect().height : previousHeight;
        const resized = Math.abs(width - previousWidth) > 1;
        previousHeight = height;
        previousWidth = width;
        this.heightAnimation?.cancel();
        this.heightAnimation = null;
        shell.removeAttribute('data-ai-moving');
        if (!this.isConnected || this.motionMedia.matches || resized || Math.abs(height - from) < 1) return;
        shell.setAttribute('data-ai-moving', '');
        const animation = shell.animate([{ height: `${from}px` }, { height: `${height}px` }],
          { duration: 320, easing: 'cubic-bezier(.22, 1, .36, 1)' });
        this.heightAnimation = animation;
        animation.onfinish = () => {
          if (this.heightAnimation !== animation) return;
          this.heightAnimation = null;
          shell.removeAttribute('data-ai-moving');
        };
      });
      this.stageObserver.observe(stage);
    }

    animatePhase(key, node) {
      if (this.motionPhase === key) return;
      const initial = this.motionPhase === undefined;
      this.motionPhase = key;
      this.phaseAnimation?.cancel();
      this.phaseAnimation = null;
      if (initial || !node || typeof node.animate !== 'function' || !this.motionMedia || this.motionMedia.matches || !this.isConnected) return;
      this.phaseAnimation = node.animate([{ opacity: .65, transform: 'translateY(4px)' }, { opacity: 1, transform: 'translateY(0)' }],
        { duration: 240, easing: 'ease-out' });
    }

    stopMotion() {
      this.heightAnimation?.cancel();
      this.phaseAnimation?.cancel();
      this.heightAnimation = null;
      this.phaseAnimation = null;
      this.querySelector('[data-ai-motion]')?.removeAttribute('data-ai-moving');
    }

    stopProgressTimer() {
      clearTimeout(this.progressTimer);
      this.progressTimer = null;
    }

    renderProgress() {
      const now = Date.now();
      const busy = ['validating', 'uploading', 'generating', 'finalizing'].includes(this.dataset.aiState);
      const waiting = this.waitingClock ? { ...this.waitingClock, now } : busy && !this.restored ? { preparing: true } : null;
      const view = progressPresentation(this.dataset.aiState, this.progressTiming, now - this.progressReceivedAt, waiting);
      this.dataset.aiWaitUnified = String(Boolean(view.unified));
      this.dataset.aiUploadPreparing = String(Boolean(view.uploading));
      const text = (selector, value) => {
        const node = this.querySelector(selector);
        if (node && node.textContent !== value) node.textContent = value;
      };
      const progress = this.querySelector('[data-ai-progress]');
      const measured = view.percent !== null;
      if (measured) {
        view.percent = Math.max(this.progressFloor || 0, view.percent);
        this.progressFloor = view.percent;
      }
      if (progress) {
        progress.max = 100;
        if (measured) progress.value = view.percent;
        else progress.removeAttribute('value');
        progress.setAttribute('aria-label', view.uploading ? 'Photo preparation in progress' : 'Estimated preview progress');
        progress.setAttribute('aria-valuetext', measured ? `Approximately ${view.percent} percent. ${view.detail}` : view.detail);
      }
      const track = this.querySelector('[data-ai-progress-track]');
      if (track) {
        const percent = measured ? view.percent : this.progressFloor || 0;
        track.style.setProperty('--ai-progress', String(percent / 100));
        track.dataset.mode = measured ? 'measured' : percent > 0 ? 'waiting' : 'scanning';
      }
      text('[data-ai-progress-title]', view.title);
      text('[data-ai-progress-eta]', view.detail);
      text('[data-ai-progress-average]', view.average);
      this.animatePhase((view.unified ? 'waiting' : this.dataset.aiState) + ':' + view.title, this.querySelector('[data-ai-processing]'));
      // Announce phase changes, not every ticking second or polling response.
      this.setStatus(view.title + '.');
    }

    showState(state, progress = null, timing = null) {
      this.dataset.aiState = state;
      const busy = ['validating', 'uploading', 'generating', 'finalizing'].includes(state);
      const now = Date.now();
      if (!busy) {
        this.stopProgressTimer();
        this.waitingClock = null;
        this.dataset.aiWaitUnified = 'false';
        this.progressFloor = 0;
        this.dataset.aiUploadPreparing = 'false';
      } else if (!this.restored && !this.waitingClock && state === 'generating' && timing?.phase === 'generating') {
        // Start only with the first generation estimate; upload has its own UI.
        this.waitingClock = { startedAt: now, deadline: now + Math.max(0, timing.expected_ms - timing.elapsed_ms) };
        this.progressFloor = 0;
      } else if (!this.restored && !this.waitingClock && state === 'finalizing') {
        // A fast completed job still waits for the actual result, without a new ETA.
        this.waitingClock = { startedAt: now, deadline: now };
      }
      const hasPhoto = Boolean(this.file || this.restored);
      const hasResult = Boolean(this.result && this.resultObjectUrl);
      this.setAttribute('aria-busy', String(busy));
      this.querySelectorAll('[data-ai-upload-empty]').forEach((node) => { node.hidden = hasPhoto || busy; });
      this.querySelectorAll('[data-ai-workspace]').forEach((node) => { node.hidden = !hasPhoto && !busy && state !== 'error'; });
      this.querySelectorAll('[data-ai-processing]').forEach((node) => { node.hidden = !busy; });
      // An uploaded source is never a generated result, including after failure.
      this.querySelectorAll('[data-ai-result], [data-ai-action="view"], [data-ai-view-hint]').forEach((node) => { node.hidden = !hasResult || busy; });
      this.querySelectorAll('[data-ai-notice]').forEach((node) => { node.hidden = busy; });
      this.querySelectorAll('[data-ai-notice-title]').forEach((node) => {
        node.textContent = state === 'error' ? (hasResult ? 'Your painting is saved' : 'Preview not created') :
          state === 'complete' ? 'Your painting preview' : 'Your photo is ready';
      });
      this.querySelectorAll('[data-ai-file-row]').forEach((node) => { node.hidden = !hasPhoto; });
      this.querySelectorAll('[data-ai-source-label]').forEach((node) => { node.textContent = this.sourceObjectUrl ? 'Your uploaded photo' : 'Saved preview'; });
      this.querySelectorAll('[data-ai-thumbnail]').forEach((node) => { node.hidden = !hasPhoto; });
      this.querySelectorAll('[data-ai-action="cancel"]').forEach((node) => { node.hidden = !busy; });
      this.querySelectorAll('[data-ai-remove-file]').forEach((node) => { node.hidden = busy; });
      const progressNode = this.querySelector('[data-ai-progress]');
      if (progressNode) {
        progressNode.hidden = !busy;
      }
      this.querySelectorAll('[data-ai-progress-track]').forEach((node) => { node.hidden = !busy; });
      this.querySelectorAll('[data-ai-submit]').forEach((button) => {
        button.disabled = busy || !hasPhoto || Boolean(this.retryBlocked);
        button.hidden = busy || !hasPhoto || state === 'complete' || Boolean(this.retryBlocked);
      });
      const messages = { empty: '', validating: 'Checking your photo…', ready: 'Your photo is ready.', uploading: 'Uploading your photo…', generating: 'Creating your preview…', complete: 'Your preview is ready.' };
      if (!busy) this.setStatus(messages[state] || '');
      if (busy) {
        this.progressTiming = timing;
        this.progressReceivedAt = Date.now();
        this.renderProgress();
        if (this.progressTimer == null && this.querySelector('[data-ai-progress]')) {
          const tick = () => {
            if (!this.isConnected || !['validating', 'uploading', 'generating', 'finalizing'].includes(this.dataset.aiState)) { this.stopProgressTimer(); return; }
            this.renderProgress();
            this.progressTimer = setTimeout(tick, 1000);
          };
          this.progressTimer = setTimeout(tick, 1000);
        }
      }
      const error = this.querySelector('[data-ai-error]');
      if (error) { error.textContent = ''; error.hidden = true; }
      const support = this.querySelector('[data-ai-error-support]');
      if (support) support.hidden = true;
      if (!busy) this.animatePhase(state, this.querySelector(state === 'empty' ? '[data-ai-upload-empty]' : '.ai-pbn-personalizer__canvas'));
      this.syncPurchaseControls();
    }

    setStatus(message) {
      this.querySelectorAll('[data-ai-status]').forEach((node) => { if (node.textContent !== message) node.textContent = message; });
    }

    showError(message, changeState = true) {
      if (changeState) this.showState('error');
      const node = this.querySelector('[data-ai-error]');
      if (node) { node.textContent = message; node.hidden = false; }
      const supportMessages = [
        'We could not create your painting. Your photo has not been turned into a preview. Please try again.',
        'The preview limit has been reached. Please try again later.',
        'Preview generation is temporarily paused. Please come back later. Your photo was not transformed.',
        'The preview service is unavailable. Please try again.',
        'AI preview is currently unavailable. Your photo has not been uploaded.'
      ];
      const support = this.querySelector('[data-ai-error-support]');
      if (support) support.hidden = Boolean(this.result) || !supportMessages.includes(message);
    }

    errorMessage(error) {
      return error instanceof PersonalizerError ? error.message : 'Something went wrong. Please try again.';
    }

    onClick(event) {
      const button = event.target instanceof Element ? event.target.closest('button') : null;
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-ai-dialog-close')) { this.closeDialog(button.closest('dialog')); return; }
      if (button.dataset.aiDialogOpen) {
        const dialog = this.dialogs.find((candidate) => candidate.id === button.dataset.aiDialogOpen);
        if (dialog) this.openDialog(dialog, button);
        return;
      }
      const action = button.dataset.aiAction;
      if (action === 'remove' || action === 'cancel' || button.hasAttribute('data-ai-remove-file')) this.reset();
      else if (action === 'retry' || action === 'generate' || button.hasAttribute('data-ai-submit')) {
        if (this.result) this.bindSelection();
        else if (this.restored && this.journal?.read()) this.restoreJob(this.journal.read());
        else this.generate();
      }
      else if (action === 'camera' || action === 'gallery' || action === 'choose' || button.hasAttribute('data-ai-choose-file')) {
        if (action === 'choose' && window.matchMedia('(max-width: 600px)').matches) {
          const chooser = this.querySelector('[data-ai-chooser]');
          if (chooser) { this.openDialog(chooser, button); return; }
        }
        window.MyArtModalMotion?.cancel(button.closest('dialog'));
        button.closest('dialog')?.close();
        const input = action === 'camera' ? this.querySelector('[data-ai-camera-file]') : this.fileInput;
        input?.click();
      } else if (action === 'zoom-in' || action === 'zoom-out' || action === 'zoom-reset') {
        this.zoomViewer(action === 'zoom-reset' ? 1 : this.zoom + (action === 'zoom-in' ? 0.5 : -0.5));
      }
    }

    closeDialog(dialog) {
      if (!dialog?.open) return;
      if (window.MyArtModalMotion) window.MyArtModalMotion.close(dialog);
      else dialog.close();
    }

    openDialog(dialog, opener) {
      if (dialog.open && !dialog.hasAttribute('data-myart-modal-closing')) return;
      dialog.aiOpener = opener;
      this.lockScroll();
      window.MyArtModalMotion ? window.MyArtModalMotion.open(dialog) : dialog.showModal();
      if (dialog === this.viewer) this.resetViewer();
      const surface = dialog.querySelector('[data-ai-dialog-surface]') || dialog;
      if (this.inputModality === 'keyboard') dialog.querySelector('[data-ai-dialog-close]')?.focus({ preventScroll: true });
      else { surface.setAttribute('tabindex', '-1'); surface.focus({ preventScroll: true }); }
    }

    afterDialogClose(dialog) {
      if (dialog.open) return;
      if (dialog === this.viewer) this.resetViewer();
      const opener = dialog.aiOpener;
      delete dialog.aiOpener;
      if (!this.dialogs.some((candidate) => candidate.open)) this.unlockScroll();
      if (this.inputModality === 'keyboard' && opener?.isConnected) opener.focus({ preventScroll: true });
      else if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    }

    lockScroll() {
      if (this.scrollLock) return;
      const body = document.body;
      const html = document.documentElement;
      const remember = (node, names) => Object.fromEntries(names.map((name) => [name, node.style[name]]));
      const viewportGutter = Math.max(0, window.innerWidth - body.getBoundingClientRect().width);
      this.scrollLock = { y: window.scrollY, body: remember(body, ['position', 'top', 'width']), html: remember(html, ['overflow', 'scrollBehavior']) };
      if (!window.matchMedia('(min-width: 750px)').matches) {
        body.style.position = 'fixed';
        body.style.top = `-${this.scrollLock.y}px`;
      }
      body.style.width = `calc(100% - ${viewportGutter}px)`;
      html.style.overflow = 'hidden';
    }

    unlockScroll() {
      if (!this.scrollLock) return;
      const state = this.scrollLock;
      this.scrollLock = null;
      for (const [node, styles] of [[document.body, state.body], [document.documentElement, state.html]]) {
        Object.entries(styles).forEach(([name, value]) => { node.style[name] = value; });
      }
      document.documentElement.style.scrollBehavior = 'auto';
      window.scrollTo(0, state.y);
      document.documentElement.style.scrollBehavior = state.html.scrollBehavior;
    }

    reset() {
      this.purchaseHelpRequested = false;
      if (this.viewer?.open) { window.MyArtModalMotion?.cancel(this.viewer); this.viewer.close(); }
      this.resetViewer();
      this.retryBlocked = false;
      this.journal?.clear();
      this.restored = false;
      this.fileRevision += 1;
      this.validation?.abort();
      this.invalidateResult();
      this.file = null;
      this.uploadFile = null;
      this.attemptKey = null;
      this.attemptVariantId = null;
      if (this.sourceObjectUrl) URL.revokeObjectURL(this.sourceObjectUrl);
      this.sourceObjectUrl = null;
      this.querySelectorAll('[data-ai-file]').forEach((input) => { input.value = ''; });
      this.setPreview(null);
      this.showState('empty');
    }

    disconnectedCallback() {
      this.resetViewer();
      this.viewerObserver?.disconnect();
      this.viewerObserver = null;
      this.stopProgressTimer();
      this.waitingClock = null;
      this.stopMotion();
      this.stageObserver?.disconnect();
      this.stageObserver = null;
      this.motionPhase = undefined;
      this.lifecycle?.abort();
      this.validation?.abort();
      this.job?.cancel();
      this.selectionRequest?.abort();
      this.dialogs?.forEach((dialog) => { window.MyArtModalMotion?.cancel(dialog); if (dialog.open) dialog.close(); });
      this.unlockScroll();
      if (this.sourceObjectUrl) URL.revokeObjectURL(this.sourceObjectUrl);
      if (this.resultObjectUrl) URL.revokeObjectURL(this.resultObjectUrl);
      this.resultObjectUrl = null;
      this.sourceObjectUrl = null;
      this.file = null;
      this.uploadFile = null;
      this.result = null;
      this.lifecycle = null;
    }
  }

  customElements.define('ai-pbn-personalizer', AiPbnPersonalizer);
})();
